import React, { useState } from 'react';
import { format } from 'date-fns';
import { convertDecimalToMinutes } from '../../../utils/coordinates';
import Tooltip from 'devextreme-react/tooltip';
import Button from 'devextreme-react/button';
import { LiveTrackingInfoWindowProp } from '../../../types/live-tracking-info-window';
import { useLocation, useNavigate } from 'react-router-dom';
import AddToFleetModal from '../../add-vessel-to-fleet/addVesselToFleet';
import { usePermission } from '../../../contexts/PermissionContext';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { PortCallResponse } from '../../../types/port-calls';
import { useDateFormat } from '../../../hooks/useDateFormat';

export default function LiveTrackingInfoWindow({
  position,
  vesselDetails,
  flag,
  t,
  showAddtoFleetPortCallButtons = false,
  isFromLiveTrackingApi = false,
  refreshWatchList,
}: LiveTrackingInfoWindowProp) {
  const { findFeature } = usePermission();
  const { setIsLoading } = useLoaderContext();
  const { UTCFormat } = useDateFormat();
  const hasViewDetailsPermission = findFeature('Ship.ViewShipDetails');
  const location = useLocation();
  const isVesselLiveTracking = location.pathname.includes(
    'vessel-live-tracking'
  );

  const dateInGMTFormat = (date: string, isUtc?: boolean) => {
    const dateObj = new Date(date);
    if (isUtc) {
      return format(dateObj, "dd MMM yyyy HH:mm:ss 'GMT'");
    }
    return format(
      new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60 * 1000),
      "dd MMM yyyy HH:mm:ss 'GMT'"
    );
  };
  const navigate = useNavigate();
  const navigateVesselDetails = () => {
    navigate(`/vessel-info`, { state: { imoNumber: vesselDetails.imoNumber } });
  };
  const [isAddToFleetModalVisible, setIsAddToFleetModalVisible] =
    useState(false);

  const handleAddToFleetClick = () => {
    setIsAddToFleetModalVisible(true);
  };

  const handleDownloadPortCalls = async () => {
    if (!vesselDetails.imoDisplayName) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get({
        url:
          `${apiConfig.portCalls}?imo=${vesselDetails.imoDisplayName}` +
          `&voyageDistanceRequired=${findFeature(
            'Ship.ViewDistanceInPortCalls'
          )}` +
          `&carbonEmissionRequired=${findFeature('Ship.ViewCarbonEmission')}` +
          '&isExcelDownload=true',
      });
      const portCalls: PortCallResponse[] = response.data || [];

      if (portCalls.length === 0) {
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Port Calls');

      worksheet.columns = [
        { header: 'Port Name', key: 'portName', width: 30 },
        { header: 'Arrival Date', key: 'arrivalDate', width: 30 },
        { header: 'Departure Date', key: 'departureDate', width: 30 },
        { header: 'Duration (hrs)', key: 'voyageDuration', width: 30 },
        { header: 'Distance (NM)', key: 'voyageDistance', width: 30 },
        {
          header: 'Estimated Carbon Emission',
          key: 'carbonEmission',
          width: 30,
        },
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { horizontal: 'center' };

      portCalls.forEach((row) => {
        worksheet.addRow({
          portName: row.portName,
          arrivalDate: row.arrivalDate
            ? UTCFormat(row.arrivalDate)?.replace(/ ?(UTC|GMT)$/, '')
            : '',
          departureDate: row.departureDate
            ? UTCFormat(row.departureDate)?.replace(/ ?(UTC|GMT)$/, '')
            : '',
          voyageDuration:
            row.voyageDuration != null
              ? Number(row.voyageDuration).toFixed(2)
              : '',
          voyageDistance:
            row.voyageDistance != null
              ? Number(row.voyageDistance).toFixed(2)
              : '',
          carbonEmission:
            row.carbonEmission != null
              ? Number(row.carbonEmission).toFixed(2)
              : '',
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `port_calls_${vesselDetails.imoDisplayName}.xlsx`);
    } catch (error) {
      console.error('Error downloading port calls:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="map-info-window-container">
      <div className="vessel-tracking-info-window-header">
        <span className="header-text">
          <span
            onClick={
              hasViewDetailsPermission && isVesselLiveTracking
                ? navigateVesselDetails
                : undefined
            }
            className={
              hasViewDetailsPermission && isVesselLiveTracking
                ? 'clickable'
                : undefined
            }
          >
            {vesselDetails?.vesselName}
          </span>
        </span>
        {vesselDetails?.isSanction && (
          // <div>
          <i className="dx-icon-warning custom-icon"></i>
          // </div>
        )}
      </div>
      <div className="time-block">
        {dateInGMTFormat(position.lastPositionUtc)}
      </div>
      <div className="flag-container">
        <p className="map-info-window-label">Flag:</p>
        <img
          src={flag}
          className="flag-image"
          alt="flag"
          height={20}
          width={20}
        />
        <p className="map-info-window-label">
          <strong>{vesselDetails.flagName}</strong>
        </p>
      </div>
      {showAddtoFleetPortCallButtons && (
        <div className="additional-link-live-tracking">
          <Button
            className="additional-link-section"
            onClick={handleDownloadPortCalls}
          >
            <a className="m-additional-link-text">
              {t('labels.portCallHistory')}
            </a>
          </Button>
          <Button
            className="additional-link-section"
            disabled={!isFromLiveTrackingApi}
            onClick={handleAddToFleetClick}
          >
            <a className="m-additional-link-text">{t('labels.addToFleet')}</a>
          </Button>
        </div>
      )}
      <p className="map-info-window-label">
        {t('labels.imo')}
        <strong>{vesselDetails.imoDisplayName}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.vesselType')}: <strong>{vesselDetails.vesselType}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.coordinates')}:{' '}
        <strong>
          {convertDecimalToMinutes(position.lat, true) +
            '/' +
            convertDecimalToMinutes(position.lon)}
        </strong>
      </p>
      <div className="horizontal-line"></div>
      <p className="map-info-window-label">
        {t('labels.lastPort')}: <strong>{position.depPort}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.intendedPort')}: <strong>{position.destPort}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.estimatedCarbonEmission')}:{' '}
        {position.carbonEmission && (
          <strong>{position.carbonEmission?.toFixed(2)} gCO&#8322;/ton</strong>
        )}
      </p>
      <div className="horizontal-line"></div>
      <p className="map-info-window-label">
        {t('labels.navigationStatus')}:{' '}
        <strong>{`${position.navigationStatus}`}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.speed')}: <strong>{`${position.speed} kn`}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.course')}: <strong>{position.course}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.heading')}: <strong>{position.heading}</strong>
      </p>
      <p className="map-info-window-label">
        {t('labels.draught')}: <strong>{`${position.currentDraught} m`}</strong>
      </p>
      <div className="horizontal-line"></div>
      <p className="map-info-window-label" id="atd">
        {t('labels.atd')}: <strong>{dateInGMTFormat(position.atdUtc)}</strong>
      </p>
      <Tooltip
        target={'#atd'}
        showEvent="dxhoverstart"
        hideEvent="dxhoverend"
        position={{ at: 'left', of: '#atd', my: 'right' }}
        contentRender={() => {
          return <div className="m-l-tooltip-wrapper">{t('tooltips.atd')}</div>;
        }}
      />
      {position.etaUtc && (
        <>
          <p className="map-info-window-label" id="eta">
            {t('labels.eta')}:{' '}
            <strong>{dateInGMTFormat(position.etaUtc)}</strong>
          </p>
          <Tooltip
            target={'#eta'}
            showEvent="dxhoverstart"
            hideEvent="dxhoverend"
            position={{ at: 'left', of: '#eta', my: 'right' }}
            contentRender={() => {
              return (
                <div className="m-l-tooltip-wrapper">{t('tooltips.eta')}</div>
              );
            }}
          />
        </>
      )}
      <AddToFleetModal
        isVisible={isAddToFleetModalVisible}
        setIsVisible={setIsAddToFleetModalVisible}
        vesselIMO={vesselDetails.imoDisplayName}
        vesselName={vesselDetails.vesselName}
        onVesselAdded={() => {
          if (refreshWatchList) {
            refreshWatchList();
          }
        }}
      />
    </div>
  );
}
